import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook to manage Web Workers for CPU-intensive tasks
 * This helps prevent blocking the main thread during heavy operations
 */
export const useWorker = () => {
  const workerRef = useRef(null);
  const callbacksRef = useRef({});
  const nextIdRef = useRef(1);

  useEffect(() => {
    // Create a worker blob that can handle different tasks
    const workerCode = `
      self.onmessage = function(e) {
        const { id, task, payload } = e.data;
        
        if (task === 'generateLabel') {
          // Import necessary libraries
          importScripts(
            'https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js',
            'https://cdn.jsdelivr.net/npm/qrcode@1.5.1/build/qrcode.min.js'
          );
          
          // Mock DOM methods needed for label generation
          self.document = {
            createElementNS: function(ns, tagName) {
              return {
                setAttribute: function() {},
                outerHTML: '<svg></svg>'
              };
            }
          };
          
          // Process the label generation
          try {
            // This is a simplified version - in a real implementation,
            // you would need to port the actual label generation logic here
            // or use a more sophisticated approach like OffscreenCanvas
            
            // For now, we'll just post back a success message
            self.postMessage({
              id,
              result: 'Label generated in worker',
              error: null
            });
          } catch (error) {
            self.postMessage({
              id,
              result: null,
              error: error.message
            });
          }
        } else {
          self.postMessage({
            id,
            result: null,
            error: 'Unknown task: ' + task
          });
        }
      };
    `;

    try {
      const blob = new Blob([workerCode], { type: 'application/javascript' });
      const workerUrl = URL.createObjectURL(blob);
      workerRef.current = new Worker(workerUrl);
      
      workerRef.current.onmessage = (e) => {
        const { id, result, error } = e.data;
        const callback = callbacksRef.current[id];
        
        if (callback) {
          if (error) {
            callback.reject(new Error(error));
          } else {
            callback.resolve(result);
          }
          delete callbacksRef.current[id];
        }
      };
      
      workerRef.current.onerror = (error) => {
        console.error('Worker error:', error);
      };
      
      return () => {
        if (workerRef.current) {
          workerRef.current.terminate();
          URL.revokeObjectURL(workerUrl);
        }
      };
    } catch (error) {
      console.error('Failed to initialize Web Worker:', error);
      return null;
    }
  }, []);

  const runWorker = useCallback((task, payload) => {
    // If worker initialization failed, return null
    if (!workerRef.current) {
      console.warn('Web Worker not available, falling back to main thread');
      return null;
    }
    
    return new Promise((resolve, reject) => {
      const id = nextIdRef.current++;
      callbacksRef.current[id] = { resolve, reject };
      
      workerRef.current.postMessage({
        id,
        task,
        payload
      });
    });
  }, []);

  return { runWorker };
};
